import React, { useEffect, useRef, useState } from 'react';

import { differenceInCalendarDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import { Modal } from '@/components';
import { Button } from '@/design-system/button';
import { DateRangePicker } from '@/design-system/date-picker';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';

import {
  isAbortError,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';
import { downloadBlob } from '../format';
import { useClientCatalog } from '../hooks/useCatalog';
import type {
  ClientRef,
  CsvFile,
  StockCsvGroupBy,
  StockInventoryCsvQuery,
} from '../types';
import { useWarehouse } from '../WarehouseContext';
import { toIsoDay } from './DateField';
import { DisabledReason } from './dialogs';
import { EnumSelect, FormField, type SelectOption } from './form';
import { ClientSelect, HubSelect, SkuSelect } from './pickers';

/**
 * "Download CSV" of the Stock page: the current stock (as the page filters
 * it, optionally grouped), the movement report or the mutation report of a
 * period. The server builds the file; this fetches it with the user's token
 * and saves it under the name the server gave it.
 */

export type StockCsvMode = 'inventory' | 'movement' | 'mutation';

/** Longest period the report endpoints accept, first and last day included. */
export const MAX_PERIOD_DAYS = 31;

const MODES: { value: StockCsvMode; label: string }[] = [
  { value: 'inventory', label: 'Inventory' },
  { value: 'movement', label: 'Pergerakan stok' },
  { value: 'mutation', label: 'Mutasi stok' },
];

/** The select's "no grouping" choice: one row per batch, no `groupBy` sent. */
const BY_BATCH = 'batch';

const GROUP_OPTIONS: SelectOption[] = [
  { value: BY_BATCH, label: 'Batch' },
  { value: 'expired_date', label: 'Tanggal expired' },
  { value: 'bin', label: 'Bin' },
  { value: 'section', label: 'Section' },
  { value: 'location', label: 'Lokasi' },
  { value: 'warehouse', label: 'Hub' },
  { value: 'vendor_name', label: 'Supplier' },
];

const NO_CODES: string[] = [];

/** Days a picked period covers (both ends included); null until it has both. */
export const periodDays = (range: DateRange | undefined): number | null =>
  range?.from && range.to
    ? differenceInCalendarDays(range.to, range.from) + 1
    : null;

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * What the Stock page shows: its hub, applied filters and search. The
   * inventory CSV is exactly that list; `groupBy` is picked here.
   */
  inventory: Omit<StockInventoryCsvQuery, 'groupBy'>;
}

const DownloadStockCsvModal: React.FC<Props> = ({ open, onClose, inventory }) =>
  // Mounted per opening: every field starts from its default.
  open ? <CsvDialog onClose={onClose} inventory={inventory} /> : null;

const CsvDialog: React.FC<Omit<Props, 'open'>> = ({ onClose, inventory }) => {
  const { hubID: currentHubID } = useWarehouse();
  const [mode, setMode] = useState<StockCsvMode>('inventory');
  const [groupBy, setGroupBy] = useState<string>(BY_BATCH);
  const [period, setPeriod] = useState<DateRange | undefined>(undefined);
  const [hubID, setHubID] = useState<number | null>(currentHubID);
  const [client, setClient] = useState<ClientRef | null>(null);
  const [sku, setSku] = useState('');
  const [busy, setBusy] = useState(false);
  const inflight = useRef<AbortController | null>(null);

  const isPeriod = mode !== 'inventory';
  const catalog = useClientCatalog({
    clientID: client?.id ?? null,
    hubID: null,
    chosen: NO_CODES,
    enabled: isPeriod,
  });

  // A download that is still running is dropped with the dialog.
  useEffect(() => () => inflight.current?.abort(), []);

  const days = periodDays(period);
  const tooLong = days !== null && days > MAX_PERIOD_DAYS;
  const periodReason = !hubID
    ? 'Pilih hub dulu.'
    : days === null
      ? 'Pilih periode laporan.'
      : undefined;
  const blocked = isPeriod && (!!periodReason || tooLong);

  const close = () => {
    inflight.current?.abort();
    onClose();
  };

  const request = (signal: AbortSignal): Promise<CsvFile> => {
    if (mode === 'inventory')
      return warehouseApi.downloadStockInventoryCsv(
        {
          ...inventory,
          groupBy: groupBy === BY_BATCH ? null : (groupBy as StockCsvGroupBy),
        },
        { signal }
      );
    const query = {
      hubID: hubID as number,
      clientID: client?.id ?? null,
      skus: sku ? [sku] : null,
      dateFrom: toIsoDay((period as DateRange).from as Date),
      dateTo: toIsoDay((period as DateRange).to as Date),
    };
    return mode === 'movement'
      ? warehouseApi.downloadStockMovementsCsv(query, { signal })
      : warehouseApi.downloadStockMutationsCsv(query, { signal });
  };

  const confirm = async () => {
    if (blocked || busy) return;
    const label = `CSV ${MODES.find((m) => m.value === mode)?.label}`;
    const controller = new AbortController();
    inflight.current = controller;
    setBusy(true);
    try {
      const { blob, filename } = await request(controller.signal);
      if (controller.signal.aborted) return;
      downloadBlob(blob, filename);
      toast.success(`${label} berhasil di-download.`);
      onClose();
    } catch (err) {
      if (isAbortError(err)) return;
      toast.error(
        `${label} gagal di-download. Silakan coba beberapa saat lagi.`,
        { description: parseWarehouseError(err).message }
      );
    } finally {
      if (inflight.current === controller) {
        inflight.current = null;
        setBusy(false);
      }
    }
  };

  return (
    <Modal
      isOpen
      onClose={close}
      icon="ri-download-2-line"
      title="Download CSV"
      width="md"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          {blocked && periodReason && (
            <DisabledReason testId="stock-csv-reason" className="mr-auto">
              {periodReason}
            </DisabledReason>
          )}
          <Button tone="neutral" style="stroke" onClick={close} disabled={busy}>
            Batal
          </Button>
          <Button
            onClick={confirm}
            disabled={blocked}
            loading={busy}
            data-testid="stock-csv-confirm"
          >
            Download CSV
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-testid="stock-csv">
        <FormField label="Jenis laporan" testId="stock-csv-mode">
          <SegmentedControl
            aria-label="Jenis laporan"
            value={mode}
            onValueChange={(v: string) => v && setMode(v as StockCsvMode)}
            className="w-full"
          >
            {MODES.map((m) => (
              <SegmentedItem key={m.value} value={m.value} className="flex-1">
                {m.label}
              </SegmentedItem>
            ))}
          </SegmentedControl>
        </FormField>

        {!isPeriod ? (
          <FormField label="Group by" testId="stock-csv-group">
            <EnumSelect
              aria-label="Group by"
              value={groupBy}
              onChange={setGroupBy}
              options={GROUP_OPTIONS}
            />
          </FormField>
        ) : (
          <>
            <FormField
              label="Periode (maks. 31 hari)"
              required
              testId="stock-csv-period"
              error={tooLong ? 'Periode maksimal 31 hari.' : null}
            >
              <DateRangePicker
                value={period}
                onValueChange={setPeriod}
                placeholder="Pilih periode"
                format="dd MMM yyyy"
              />
            </FormField>
            <FormField label="Hub" required testId="stock-csv-hub">
              <HubSelect value={hubID} onChange={setHubID} />
            </FormField>
            <FormField label="Client" optional testId="stock-csv-client">
              <ClientSelect
                value={client}
                onChange={(c) => {
                  setClient(c);
                  setSku('');
                }}
                placeholder="Semua client"
                allowClear
              />
            </FormField>
            <FormField label="SKU" optional testId="stock-csv-sku">
              <SkuSelect
                catalog={catalog}
                value={sku}
                onPick={(s) => {
                  catalog.remember(s);
                  setSku(s.sku);
                }}
                allowClear
                onClear={() => setSku('')}
                disabled={!client}
                placeholder={client ? 'Semua SKU' : 'Pilih client dulu'}
              />
            </FormField>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DownloadStockCsvModal;
